export const SUPPORTED_LOCALES = ['en', 'pt', 'es', 'ko', 'fr', 'it', 'de', 'tr', 'vi', 'ja', 'zh-TW', 'pl'];

export const PROFILE_LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'pt', label: 'Português' },
  { code: 'es', label: 'Español' },
  { code: 'ko', label: '한국어' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'de', label: 'Deutsch' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'ja', label: '日本語' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'pl', label: 'Polski' },
];

export const LOCALE_PREFERENCE_KEY = 'baronbuff_locale_preference';

const LOCALE_ALIASES = {
  'pt-br': 'pt',
  'pt-pt': 'pt',
  'es-es': 'es',
  'es-mx': 'es',
  'es-ar': 'es',
  'es-cl': 'es',
  'es-co': 'es',
  'ko-kr': 'ko',
  'fr-fr': 'fr',
  'fr-ca': 'fr',
  'it-it': 'it',
  'de-de': 'de',
  'tr-tr': 'tr',
  'vi-vn': 'vi',
  'ja-jp': 'ja',
  'zh-tw': 'zh-TW',
  'zh-hant': 'zh-TW',
  'zh-hk': 'zh-TW',  // Hong Kong falls back to Taiwan TC
};

const REGION_TO_LOCALE = {
  br: 'pt',
  br1: 'pt',
  la1: 'es',
  la2: 'es',
  kr: 'ko',
  tr: 'tr',
  tr1: 'tr',
  jp: 'ja',
  jp1: 'ja',
  tw: 'zh-TW',
  tw2: 'zh-TW',
};

const REGION_ALIASES = {
  br: 'br1',
  br1: 'br1',
  la1: 'la1',
  la2: 'la2',
  kr: 'kr',
  tr: 'tr1',
  tr1: 'tr1',
  na: 'na1',
  na1: 'na1',
  euw: 'euw1',
  euw1: 'euw1',
  eun: 'eun1',
  eune: 'eun1',
  eun1: 'eun1',
  oc: 'oc1',
  oce: 'oc1',
  oc1: 'oc1',
  jp: 'jp1',
  jp1: 'jp1',
  tw: 'tw2',
  tw2: 'tw2',
  ru: 'ru',
};

export function normalizeSupportedLocale(locale) {
  if (!locale || typeof locale !== 'string') return null;
  const normalized = locale.trim().toLowerCase().replace('_', '-');
  const alias = LOCALE_ALIASES[normalized];
  if (alias && SUPPORTED_LOCALES.includes(alias)) return alias;
  const base = normalized.split('-')[0];
  return SUPPORTED_LOCALES.includes(base) ? base : null;
}

export function explicitLocaleFromSearch(search) {
  return normalizeSupportedLocale(new URLSearchParams(search).get('lang'));
}

export function postGameReportPath(reviewId, locale) {
  const params = new URLSearchParams({ lang: normalizeSupportedLocale(locale) || 'en' });
  return `/post-game/${encodeURIComponent(reviewId)}?${params}`;
}

function defaultStorage() {
  return typeof localStorage !== 'undefined' ? localStorage : null;
}

export function readStoredLocalePreference(storage = defaultStorage()) {
  if (!storage) return null;
  try {
    return normalizeSupportedLocale(storage.getItem(LOCALE_PREFERENCE_KEY));
  } catch {
    return null;
  }
}

export function writeStoredLocalePreference(locale, storage = defaultStorage()) {
  const normalized = normalizeSupportedLocale(locale);
  if (!storage || !normalized) return null;
  try {
    storage.setItem(LOCALE_PREFERENCE_KEY, normalized);
    return normalized;
  } catch {
    return null;
  }
}

function primaryLinkedAccount(profile) {
  const accounts = profile?.linked_accounts || [];
  if (!accounts.length) return null;
  return accounts.find((account) => account.is_primary) || accounts[0];
}

export function localeFromLinkedRegion(profile) {
  const primary = primaryLinkedAccount(profile);
  const rawRegion = primary?.region;
  if (!rawRegion || typeof rawRegion !== 'string') return null;
  const key = rawRegion.trim().toLowerCase();
  const normalizedRegion = REGION_ALIASES[key] || key;
  return REGION_TO_LOCALE[normalizedRegion] || null;
}

export function localeFromBrowser(browserCandidates) {
  const candidates = browserCandidates
    || (typeof navigator !== 'undefined'
      ? [...(navigator.languages || []), navigator.language]
      : []);

  for (const candidate of candidates) {
    const locale = normalizeSupportedLocale(candidate);
    if (locale) return locale;
  }
  return null;
}

export function localeToPersistForProfile(profile, currentLocale, browserCandidates, storedPreference) {
  if (normalizeSupportedLocale(profile?.locale)) return null;

  const stored = normalizeSupportedLocale(storedPreference);
  if (stored) return stored;

  const active = normalizeSupportedLocale(currentLocale);
  if (active && active !== 'en') return active;

  const browser = localeFromBrowser(browserCandidates);
  if (browser) return browser;

  return localeFromLinkedRegion(profile) || active || 'en';
}

export function localeToApplyFromProfile(profile) {
  return normalizeSupportedLocale(profile?.locale) || localeFromLinkedRegion(profile);
}

export function localeForToolDefault({
  explicitLocale,
  referrerLocale,
  browserCandidates,
  storedPreference,
  profile,
} = {}) {
  const explicit = normalizeSupportedLocale(explicitLocale);
  if (explicit) return explicit;

  const stored = normalizeSupportedLocale(storedPreference);
  if (stored) return stored;

  const referrer = normalizeSupportedLocale(referrerLocale);
  if (referrer) return referrer;

  const browser = localeFromBrowser(browserCandidates);
  if (browser) return browser;

  return localeFromLinkedRegion(profile) || 'en';
}
